import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

export default function VideoViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const item = location?.state?.item || location?.state?.game || null;
  const [playing, setPlaying] = useState(false);
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-8" style={{ background: 'var(--color-background)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
          <Icon name="AlertCircle" size={32} color="var(--color-error)" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Content could not be loaded.</h2>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>The video failed to load. Please try again.</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <button onClick={() => setLoadError(false)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-primary)', color: '#fff' }}>
            <Icon name="RefreshCw" size={15} color="#fff" /> Retry
          </button>
          <button onClick={() => navigate('/creator-dashboard')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="LayoutDashboard" size={15} /> Back to Dashboard
          </button>
          <button onClick={() => navigate('/creator-dashboard')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="Library" size={15} /> Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-[var(--color-muted)]"
          aria-label="Go back"
        >
          <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            {item?.title || 'Video Viewer'}
          </h1>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Video</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="LayoutDashboard" size={13} />
            Dashboard
          </button>
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Library" size={13} />
            Library
          </button>
        </div>
      </div>
      {/* Video Player */}
      <div className="flex-1 flex flex-col items-center justify-start p-4 md:p-8 gap-6 max-w-4xl mx-auto w-full">
        <div
          className="w-full rounded-2xl overflow-hidden relative"
          style={{ background: '#000', aspectRatio: '16/9', border: '1px solid var(--color-border)' }}
        >
          {item?.thumbnail ? (
            <>
              <Image
                src={item?.thumbnail}
                alt={item?.thumbnailAlt || item?.title || 'Video thumbnail'}
                className="w-full h-full object-cover"
                style={{ opacity: playing ? 0.3 : 1, transition: 'opacity 0.3s' }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                {!playing ? (
                  <button
                    onClick={() => setPlaying(true)}
                    className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                    style={{ background: 'var(--color-primary)' }}
                    aria-label="Play video"
                  >
                    <Icon name="Play" size={28} color="#fff" />
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.7)' }}>
                      <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--color-primary)' }} />
                      <span className="text-white text-sm font-medium">Playing...</span>
                    </div>
                    <button
                      onClick={() => setPlaying(false)}
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.2)' }}
                      aria-label="Pause video"
                    >
                      <Icon name="Pause" size={20} color="#fff" />
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Icon name="Video" size={48} color="rgba(255,255,255,0.3)" />
                <p className="text-white/50 text-sm mt-2">No preview available</p>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        {item && (
          <div className="w-full rounded-xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title}</h2>
            {item?.prompt && <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{item?.prompt}</p>}
            {item?.createdAt && <p className="text-xs mt-2" style={{ color: 'var(--color-muted-foreground)' }}>Created: {item?.createdAt}</p>}
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = item?.thumbnail || '#';
              link.download = `${item?.title || 'video'}.mp4`;
              link?.click();
            }}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Download" size={20} color="var(--color-primary)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Download</span>
          </button>
          <button
            onClick={() => {
              if (navigator?.share) {
                navigator.share({ title: item?.title, url: window.location?.href });
              } else {
                navigator?.clipboard?.writeText(window.location?.href);
              }
            }}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Share2" size={20} color="var(--color-secondary)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Share</span>
          </button>
          <button
            onClick={() => navigate('/creator-dashboard', { state: { remixItem: item } })}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Shuffle" size={20} color="var(--color-accent)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Remix</span>
          </button>
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Library" size={20} color="var(--color-muted-foreground)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Library</span>
          </button>
        </div>
      </div>
    </div>
  );
}
